import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import FlowEnginePanel from "./components/FlowEnginePanel";
import {
  FLOW_MODULE_DEFINITIONS,
  isFlowEngineEnabledForProcess,
  resolveFlowRoleContext,
  canInboxEdit,
  resolveInboxSurfaceFromRoleName,
  canPoolOperate,
  normalizePoolSurface,
  resolvePoolSurfaceFromPath
} from "../../app/helpers/flowEngineConfigHelper";
import { resolveAuthUserId, resolveOperatingFlowRoleId } from "../../app/helpers/flowEditPageHelper";
import { canOperateCmiRecord, canOperateTrayView } from "../../app/helpers/flowTraySurfaceHelper";
import { normalizeSpecialistRoleKey } from "../../app/helpers/activeSpecialistRoleHelper";
import { readFichaReturnContext } from "../../app/helpers/moduleFichaNavHelper";
import { useIntegrationNestedPreview } from "../end-user-search/integrationNestedPreviewContext";

const FlowEngineSection = ({
  record,
  moduleProcess,
  moduleName,
  selectedRecords = [],
  onAfterAction,
  layoutMode = "list",
  showActions = true,
  showSummary = false,
  showHistoryButton = true,
  compact = false,
  roleId: trayRoleId = null,
  poolSurface: poolSurfaceProp = null,
  subjectKind: subjectKindProp = ""
}) => {
  const configuration = useSelector((state) => state.configuration);
  const userRole = useSelector((state) => state.userRole);
  const auth = useSelector((state) => state.auth);
  const catalogRoles = useSelector((state) => state.catalogRole?.catalogRoles || []);
  const location = useLocation();
  const { nested } = useIntegrationNestedPreview();

  const moduleDef = FLOW_MODULE_DEFINITIONS[moduleProcess] || {};
  const engineEnabled = isFlowEngineEnabledForProcess(configuration, moduleProcess);
  const isEndUserSearch = String(location.pathname || "").includes("/end-user-search");
  const fromSupportInbox = new URLSearchParams(location.search || "").get("from") === "support-inbox";

  const roleContext = useMemo(
    () => resolveFlowRoleContext({ userRole, authUser: auth?.user }),
    [userRole, auth]
  );

  const operatingRoleId = resolveOperatingFlowRoleId({
    record,
    selectedRecords,
    catalogRoles,
    trayRoleId,
    roleContext
  });
  const operatingRole = catalogRoles.find((role) => Number(role.id) === Number(operatingRoleId));
  const operatingRoleName = operatingRole?.name || roleContext.roleName;
  const inboxSurface = resolveInboxSurfaceFromRoleName(operatingRoleName, roleContext.isAdmin);
  const selectedRole = userRole?.roleLogin?.roleSpecialistSelected;
  const selectedRoleKey = normalizeSpecialistRoleKey(selectedRole) || roleContext.roleCode;
  const navOrigin = location.state?.origin
    || resolvePoolSurfaceFromPath(location.state?.returnTo || readFichaReturnContext(moduleProcess).returnTo || location.pathname);
  const poolSurface = normalizePoolSurface(poolSurfaceProp)
    || normalizePoolSurface(inboxSurface)
    || normalizePoolSurface(navOrigin);
  const trayView = location.state?.trayView
    || readFichaReturnContext(moduleProcess).trayView
    || '';
  const gateRecords = (selectedRecords || []).length
    ? selectedRecords
    : (record ? [record] : []);
  const authUserId = resolveAuthUserId(auth?.user);
  const canOperateByCompetence = gateRecords.length > 0
    && gateRecords.every((row) => canOperateCmiRecord({
      record: row,
      roleKey: selectedRoleKey,
      roleId: selectedRole?.id || roleContext.roleId,
      userId: authUserId
    }));
  const subjectKind = subjectKindProp || record?.subject_kind || selectedRecords[0]?.subject_kind || "";
  const feederTermOperate = subjectKind === "search_key_term" && selectedRoleKey === "feeder";
  const canOperateInbox = feederTermOperate
    ? roleContext.canOperate && canOperateByCompetence
    : roleContext.canOperate
    && canInboxEdit(configuration, moduleProcess, inboxSurface || poolSurface, roleContext.isAdmin)
    && canPoolOperate(configuration, poolSurface, selectedRoleKey)
    && canOperateTrayView({
      trayView,
      selectedRoleKey,
      canOperatePool: true
    })
    && canOperateByCompetence;

  if (!engineEnabled || nested || isEndUserSearch || fromSupportInbox) {
    return null;
  }

  return (
    <FlowEnginePanel
      record={record}
      moduleId={moduleDef.moduleId}
      moduleProcess={moduleProcess}
      moduleName={moduleName}
      roleId={operatingRoleId || roleContext.roleId}
      roleCode={operatingRoleName}
      roleName={operatingRoleName}
      canOperate={canOperateInbox}
      poolSurface={poolSurface}
      activeCatalogRoleId={selectedRole?.id || roleContext.roleId}
      activeRoleKey={selectedRoleKey}
      selectedRecords={selectedRecords}
      onAfterAction={onAfterAction}
      layoutMode={layoutMode}
      showActions={showActions}
      showSummary={showSummary}
      showHistoryButton={showHistoryButton}
      compact={compact}
      subjectKind={subjectKind}
    />
  );
};

FlowEngineSection.propTypes = {
  record: PropTypes.object,
  moduleProcess: PropTypes.string.isRequired,
  moduleName: PropTypes.string,
  selectedRecords: PropTypes.array,
  onAfterAction: PropTypes.func,
  layoutMode: PropTypes.oneOf(["list", "edit"]),
  showActions: PropTypes.bool,
  showSummary: PropTypes.bool,
  showHistoryButton: PropTypes.bool,
  compact: PropTypes.bool,
  roleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  poolSurface: PropTypes.string,
  subjectKind: PropTypes.string
};

export default FlowEngineSection;
